import React, { useState, useEffect } from 'react';
import { Home, Clock, Heart, Gift, Check, HelpCircle, Play, ChevronRight, Star, Wind } from 'lucide-react';

// Child Profile Data
const childProfile = {
  name: "Alex",
  tokens: 245,
  streak: 7,
  level: 5
};

// Sample schedule data
const morningRoutine = {
  name: "Morning Routine",
  steps: [
    { id: 1, title: "Wake Up", icon: "🌅", completed: true, tokens: 2 },
    { id: 2, title: "Use Bathroom", icon: "🚿", completed: true, tokens: 2 },
    { id: 3, title: "Brush Teeth", icon: "🦷", completed: false, tokens: 3, timer: 120, current: true },
    { id: 4, title: "Get Dressed", icon: "👕", completed: false, tokens: 3 },
    { id: 5, title: "Eat Breakfast", icon: "🥣", completed: false, tokens: 3 },
    { id: 6, title: "Pack Backpack", icon: "🎒", completed: false, tokens: 2 }
  ]
};

const emotions = [
  { name: "Happy", emoji: "😊", color: "#4CAF50" },
  { name: "Sad", emoji: "😢", color: "#2196F3" },
  { name: "Angry", emoji: "😠", color: "#F44336" },
  { name: "Scared", emoji: "😨", color: "#9C27B0" },
  { name: "Frustrated", emoji: "😤", color: "#FF9800" },
  { name: "Excited", emoji: "🤩", color: "#E91E63" },
  { name: "Calm", emoji: "😌", color: "#00BCD4" },
  { name: "Tired", emoji: "😴", color: "#607D8B" },
  { name: "Confused", emoji: "😕", color: "#795548" }
];

const rewards = [
  { id: 1, name: "30 min iPad", cost: 50, icon: "📱", affordable: true },
  { id: 2, name: "Movie Night", cost: 100, icon: "🎬", affordable: true },
  { id: 3, name: "Ice Cream", cost: 30, icon: "🍦", affordable: true },
  { id: 4, name: "Park Trip", cost: 150, icon: "🏞️", affordable: true },
  { id: 5, name: "New Toy", cost: 300, icon: "🧸", affordable: false },
  { id: 6, name: "Sleepover", cost: 500, icon: "🏠", affordable: false }
];

// Timer Component
const VisualTimer: React.FC<{ seconds: number; isActive: boolean }> = ({ seconds, isActive }) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const percentage = (timeLeft / seconds) * 100;

  useEffect(() => {
    if (!isActive || timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [isActive, timeLeft]);

  const minutes = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full transform -rotate-90">
        <circle cx="64" cy="64" r="56" fill="none" stroke="#E0E0E0" strokeWidth="12" />
        <circle
          cx="64" cy="64" r="56" fill="none"
          stroke={percentage > 30 ? "#4CAF50" : percentage > 10 ? "#FF9800" : "#F44336"}
          strokeWidth="12"
          strokeDasharray={`${percentage * 3.51} 351`}
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-700">
          {minutes}:{secs.toString().padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

// Navigation Button
const NavButton: React.FC<{ icon: React.ElementType; label: string; active: boolean; onClick: () => void }> = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center p-3 rounded-xl transition-all ${
      active ? 'bg-green-500 text-white' : 'text-gray-600 hover:bg-gray-100'
    }`}
  >
    <Icon size={28} />
    <span className="text-xs mt-1 font-medium">{label}</span>
  </button>
);

// Home Screen
const HomeScreen: React.FC<{ onStartRoutine: () => void }> = ({ onStartRoutine }) => (
  <div className="flex flex-col h-full">
    <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl">
            👦
          </div>
          <div>
            <p className="text-green-100 text-sm">Good morning!</p>
            <p className="font-bold text-lg">{childProfile.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white/20 px-3 py-2 rounded-full">
          <Star className="text-yellow-300" size={20} fill="currentColor" />
          <span className="font-bold">{childProfile.tokens}</span>
        </div>
      </div>
    </div>

    <div className="flex-1 p-4 overflow-auto">
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
        <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
          <Clock size={16} />
          <span>Now</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-6xl">🌅</div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800">Morning Routine</h2>
            <p className="text-gray-500">6 steps • Earn up to 15 ⭐</p>
          </div>
        </div>
        <button
          onClick={onStartRoutine}
          className="w-full mt-4 bg-green-500 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-green-600 transition-colors"
        >
          <Play size={24} fill="white" />
          Start
        </button>
      </div>

      <div className="bg-gray-50 rounded-2xl p-4">
        <p className="text-gray-500 text-sm mb-3">Up Next</p>
        <div className="flex items-center gap-3 bg-white rounded-xl p-3">
          <div className="text-3xl">📚</div>
          <div className="flex-1">
            <p className="font-medium text-gray-800">School Time</p>
            <p className="text-sm text-gray-500">8:30 AM</p>
          </div>
          <ChevronRight className="text-gray-400" />
        </div>
      </div>
    </div>
  </div>
);

// Schedule Execution Screen
const ScheduleScreen: React.FC<{ onComplete: () => void; onBack: () => void }> = ({ onComplete, onBack }) => {
  const [currentStep, setCurrentStep] = useState(2);
  const [timerActive, setTimerActive] = useState(true);
  const step = morningRoutine.steps[currentStep];

  const handleComplete = () => {
    if (currentStep < morningRoutine.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-blue-50 to-white">
      {/* Progress bar */}
      <div className="bg-white p-3 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={onBack} className="text-gray-500">←</button>
          <span className="font-medium text-gray-700">{morningRoutine.name}</span>
        </div>
        <div className="flex gap-1">
          {morningRoutine.steps.map((s, i) => (
            <div
              key={s.id}
              className={`flex-1 h-2 rounded-full ${
                i < currentStep ? 'bg-green-500' :
                i === currentStep ? 'bg-blue-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className="text-center text-sm text-gray-500 mt-1">
          Step {currentStep + 1} of {morningRoutine.steps.length}
        </p>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="text-9xl mb-4">{step.icon}</div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{step.title}</h1>

        {step.timer && (
          <div className="my-4">
            <VisualTimer seconds={step.timer} isActive={timerActive} />
          </div>
        )}

        <div className="flex items-center gap-2 text-yellow-600 mb-6">
          <Star size={20} fill="currentColor" />
          <span className="font-bold">+{step.tokens} tokens</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="p-4 bg-white shadow-lg">
        <button
          onClick={handleComplete}
          className="w-full bg-green-500 text-white py-5 rounded-2xl font-bold text-xl flex items-center justify-center gap-3 hover:bg-green-600 transition-colors mb-3"
        >
          <Check size={28} strokeWidth={3} />
          Done!
        </button>
        <button className="w-full flex items-center justify-center gap-2 text-gray-500 py-2">
          <HelpCircle size={20} />
          <span>I need help</span>
        </button>
      </div>
    </div>
  );
};

// Feelings Screen
const FeelingsScreen: React.FC = () => {
  const [selectedEmotion, setSelectedEmotion] = useState<typeof emotions[0] | null>(null);
  const [intensity, setIntensity] = useState(3);
  const [showCalming, setShowCalming] = useState(false);

  if (showCalming) {
    return (
      <div className="flex flex-col h-full bg-gradient-to-b from-cyan-50 to-white p-4">
        <button onClick={() => setShowCalming(false)} className="self-start text-gray-500 mb-4">← Back</button>
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Let's Calm Down</h2>

        <div className="grid grid-cols-2 gap-4">
          {[
            { name: "Bubble Breathing", icon: "🫧", color: "bg-blue-100" },
            { name: "Squeeze Ball", icon: "🔴", color: "bg-red-100" },
            { name: "Count to 10", icon: "🔢", color: "bg-purple-100" },
            { name: "Nature Sounds", icon: "🌲", color: "bg-green-100" },
            { name: "Pop Bubbles", icon: "💭", color: "bg-pink-100" },
            { name: "Deep Breaths", icon: "💨", color: "bg-cyan-100" }
          ].map((activity, i) => (
            <button key={i} className={`${activity.color} p-6 rounded-2xl flex flex-col items-center gap-2 hover:scale-105 transition-transform`}>
              <span className="text-5xl">{activity.icon}</span>
              <span className="font-medium text-gray-700">{activity.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-pink-50 to-white p-4">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">How are you feeling?</h2>
      <p className="text-center text-gray-500 mb-6">Tap the face that matches your feeling</p>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {emotions.map((emotion) => (
          <button
            key={emotion.name}
            onClick={() => setSelectedEmotion(emotion)}
            className={`p-4 rounded-2xl flex flex-col items-center transition-all ${
              selectedEmotion?.name === emotion.name
                ? 'ring-4 ring-offset-2 scale-105'
                : 'bg-white shadow-md hover:shadow-lg'
            }`}
            style={{
              backgroundColor: selectedEmotion?.name === emotion.name ? emotion.color + '20' : undefined,
            }}
          >
            <span className="text-4xl mb-1">{emotion.emoji}</span>
            <span className="text-sm font-medium text-gray-700">{emotion.name}</span>
          </button>
        ))}
      </div>

      {selectedEmotion && (
        <div className="bg-white rounded-2xl p-4 shadow-lg mb-4">
          <p className="text-center text-gray-600 mb-3">How {selectedEmotion.name.toLowerCase()} do you feel?</p>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((level) => (
              <button
                key={level}
                onClick={() => setIntensity(level)}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-xl transition-all ${
                  intensity >= level
                    ? 'scale-110'
                    : 'bg-gray-100'
                }`}
                style={{
                  backgroundColor: intensity >= level ? selectedEmotion.color : undefined,
                  opacity: intensity >= level ? 0.3 + (level * 0.15) : 1
                }}
              >
                {level}
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">
            {intensity <= 2 ? "A little" : intensity <= 3 ? "Somewhat" : intensity <= 4 ? "Quite a bit" : "A lot"}
          </p>
        </div>
      )}

      {selectedEmotion && ["Angry", "Sad", "Scared", "Frustrated"].includes(selectedEmotion.name) && (
        <button
          onClick={() => setShowCalming(true)}
          className="w-full bg-cyan-500 text-white py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-cyan-600 transition-colors"
        >
          <Wind size={24} />
          Help me calm down
        </button>
      )}
    </div>
  );
};

// Rewards Screen
const RewardsScreen: React.FC = () => {
  const [selectedReward, setSelectedReward] = useState<typeof rewards[0] | null>(null);

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-yellow-50 to-white">
      <div className="bg-gradient-to-r from-yellow-400 to-orange-400 p-6 text-white text-center">
        <p className="text-yellow-100">Your Tokens</p>
        <div className="flex items-center justify-center gap-2 my-2">
          <Star size={36} fill="white" />
          <span className="text-5xl font-bold">{childProfile.tokens}</span>
        </div>
        <p className="text-yellow-100 text-sm">{childProfile.streak} day streak! 🔥</p>
      </div>

      <div className="flex-1 p-4 overflow-auto">
        <h3 className="font-bold text-gray-700 mb-3">Rewards Store</h3>
        <div className="grid grid-cols-2 gap-3">
          {rewards.map((reward) => (
            <button
              key={reward.id}
              onClick={() => reward.affordable && setSelectedReward(reward)}
              className={`bg-white rounded-2xl p-4 shadow-md flex flex-col items-center transition-all ${
                reward.affordable ? 'hover:shadow-lg hover:scale-105' : 'opacity-50'
              } ${selectedReward?.id === reward.id ? 'ring-4 ring-green-400' : ''}`}
            >
              <span className="text-5xl mb-2">{reward.icon}</span>
              <span className="font-medium text-gray-800 text-sm">{reward.name}</span>
              <div className="flex items-center gap-1 mt-1">
                <Star size={14} className="text-yellow-500" fill="currentColor" />
                <span className={`text-sm font-bold ${reward.affordable ? 'text-green-600' : 'text-gray-400'}`}>
                  {reward.cost}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {selectedReward && (
        <div className="p-4 bg-white shadow-lg border-t">
          <button className="w-full bg-green-500 text-white py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-green-600 transition-colors">
            <Gift size={24} />
            Get {selectedReward.name} for {selectedReward.cost} ⭐
          </button>
        </div>
      )}
    </div>
  );
};

// Timer Standalone Screen
const TimerScreen: React.FC = () => {
  const [duration, setDuration] = useState(300);
  const [isRunning, setIsRunning] = useState(false);

  const presets = [
    { label: "1 min", seconds: 60 },
    { label: "2 min", seconds: 120 },
    { label: "5 min", seconds: 300 },
    { label: "10 min", seconds: 600 },
    { label: "15 min", seconds: 900 }
  ];

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-indigo-50 to-white items-center justify-center p-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-8">Timer</h2>

      <div className="mb-8">
        <div className="relative w-48 h-48">
          <svg className="w-full h-full transform -rotate-90">
            <circle cx="96" cy="96" r="88" fill="none" stroke="#E0E0E0" strokeWidth="16" />
            <circle
              cx="96" cy="96" r="88" fill="none"
              stroke={isRunning ? "#4CAF50" : "#9E9E9E"}
              strokeWidth="16"
              strokeDasharray={isRunning ? "400 553" : "553 553"}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl font-bold text-gray-700">
              {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {presets.map((preset) => (
          <button
            key={preset.seconds}
            onClick={() => setDuration(preset.seconds)}
            className={`px-4 py-2 rounded-full font-medium transition-all ${
              duration === preset.seconds
                ? 'bg-indigo-500 text-white'
                : 'bg-white text-gray-600 shadow-md hover:shadow-lg'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <button
        onClick={() => setIsRunning(!isRunning)}
        className={`w-48 py-4 rounded-2xl font-bold text-xl flex items-center justify-center gap-2 transition-colors ${
          isRunning
            ? 'bg-red-500 hover:bg-red-600 text-white'
            : 'bg-green-500 hover:bg-green-600 text-white'
        }`}
      >
        {isRunning ? 'Stop' : 'Start'}
      </button>
    </div>
  );
};

// Main App Component
const MockupPage: React.FC = () => {
  const [currentScreen, setCurrentScreen] = useState('home');
  const [showCelebration, setShowCelebration] = useState(false);

  const handleCompleteRoutine = () => {
    setShowCelebration(true);
    setTimeout(() => {
      setShowCelebration(false);
      setCurrentScreen('home');
    }, 3000);
  };

  // Celebration overlay
  if (showCelebration) {
    return (
      <div className="w-full max-w-md mx-auto h-[700px] bg-gradient-to-b from-green-400 to-green-600 rounded-3xl shadow-2xl flex flex-col items-center justify-center text-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute text-4xl animate-bounce"
              style={{
                left: `${Math.random() * 80 + 10}%`,
                top: `${Math.random() * 80 + 10}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${0.5 + Math.random() * 0.5}s`
              }}
            >
              {['⭐', '🎉', '✨', '🌟', '🎊'][Math.floor(Math.random() * 5)]}
            </div>
          ))}
        </div>
        <div className="text-8xl mb-4">🏆</div>
        <h1 className="text-4xl font-bold mb-2">Amazing!</h1>
        <p className="text-xl text-green-100">You finished your routine!</p>
        <div className="mt-6 bg-white/20 px-6 py-3 rounded-full flex items-center gap-2">
          <Star fill="white" />
          <span className="font-bold text-xl">+15 tokens earned!</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md h-[700px] bg-gray-100 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Screen content */}
        <div className="flex-1 overflow-hidden">
          {currentScreen === 'home' && (
            <HomeScreen onStartRoutine={() => setCurrentScreen('schedule')} />
          )}
          {currentScreen === 'schedule' && (
            <ScheduleScreen
              onComplete={handleCompleteRoutine}
              onBack={() => setCurrentScreen('home')}
            />
          )}
          {currentScreen === 'timer' && <TimerScreen />}
          {currentScreen === 'feelings' && <FeelingsScreen />}
          {currentScreen === 'rewards' && <RewardsScreen />}
        </div>

        {/* Bottom navigation */}
        {currentScreen !== 'schedule' && (
          <div className="bg-white border-t border-gray-200 px-4 py-2 flex justify-around">
            <NavButton icon={Home} label="Home" active={currentScreen === 'home'} onClick={() => setCurrentScreen('home')} />
            <NavButton icon={Clock} label="Timer" active={currentScreen === 'timer'} onClick={() => setCurrentScreen('timer')} />
            <NavButton icon={Heart} label="Feelings" active={currentScreen === 'feelings'} onClick={() => setCurrentScreen('feelings')} />
            <NavButton icon={Gift} label="Rewards" active={currentScreen === 'rewards'} onClick={() => setCurrentScreen('rewards')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MockupPage;
